import { useFormikContext } from 'formik'
import { useCallback, useEffect, useRef } from 'react'

import utils from '@lib/utils'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useSeatAncillaryContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatAncillary'
import { SetSeatsFn } from '@pages/Checkout/SeatSelection/NewSeats/context/seatSelection'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'

interface UseSelectedSeatsReturn {
  seats: Seat.ByTripDirection | null
  setSeats: SetSeatsFn
}

export const useSelectedSeats = (enabled: boolean, preselectedSeats?: Seat.ByTripDirection): UseSelectedSeatsReturn => {
  const {
    values: { seats },
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()
  const [{ seatSelection }] = useSettings()
  const { activeAncillaries, ancillaries, isAncillaryActive } = useSeatAncillaryContext()
  const [{ inbound, outbound }] = useCheckout()
  const prevActiveAncillaries = useRef(activeAncillaries)

  const setSeats: SetSeatsFn = useCallback(
    valueOrFn => {
      const value = typeof valueOrFn === 'function' ? valueOrFn(seats) : valueOrFn
      setFieldValue('seats', value || { inbound: [], outbound: [] })
    },
    [setFieldValue, seats],
  )

  useEffect(() => {
    const updateSeatsBasedOnAncillaries = () => {
      setSeats(currentSeats => {
        function updateSeatsForConnection(
          connection: Connection | null,
          seats: Seat.BySegment,
          preselectedSeats: Seat.BySegment,
        ): Seat.BySegment {
          if (!connection) return {}

          return utils.object.compact(
            Object.fromEntries(
              connection.segments.map(segment => {
                let seatsForSegment = seats[segment.index] || []

                const { id: segmentId }: Segment = segment
                const relevantAncillaries = ancillaries.filter(ancillary =>
                  ancillary.segments?.some(({ id }) => id === segmentId),
                )

                const hasActiveRelevantAncillary = relevantAncillaries.some(isAncillaryActive)
                const hadActiveRelevantAncillary = relevantAncillaries.some(ancillary =>
                  prevActiveAncillaries.current.includes(ancillary),
                )

                if (hasActiveRelevantAncillary) {
                  if (!seatsForSegment.length && !hadActiveRelevantAncillary) {
                    seatsForSegment = preselectedSeats[segment.index] || []
                  }
                } else {
                  seatsForSegment = []
                }

                return [segment.index, seatsForSegment]
              }),
            ) as Seat.BySegment,
          )
        }

        return {
          outbound: updateSeatsForConnection(outbound, currentSeats?.outbound || {}, preselectedSeats?.outbound || {}),
          inbound: updateSeatsForConnection(inbound, currentSeats?.inbound || {}, preselectedSeats?.inbound || {}),
        }
      })
    }

    if (activeAncillaries !== prevActiveAncillaries.current) {
      if (enabled && !seatSelection.mandatory) {
        updateSeatsBasedOnAncillaries()
      }

      prevActiveAncillaries.current = activeAncillaries
    }
  }, [
    activeAncillaries,
    seatSelection.mandatory,
    setSeats,
    ancillaries,
    isAncillaryActive,
    outbound,
    inbound,
    preselectedSeats,
    enabled,
  ])

  return { seats, setSeats }
}
